import React, { useRef, useState } from "react";
import { FormattedMessage, FormattedNumber, useIntl } from "react-intl";
import { ColumnDefinition, RowDefinition, components } from "griddle-react";
import { ButtonGroup, Button } from "reactstrap";
import { Map } from "immutable";
import { Link } from "react-router-dom";

import { Track } from "@music/models";
import { messages as mMessages } from "@music/messages";
import { messages as vMessages } from "@vendor/messages";
import { Playlist } from "@vendor/models";
import { messages as uiMessages } from "@ui/messages";
import { CellExpandButton, makeExpandableCellComponent, Expandable as Table } from "@ui/Table/Expandable";
import { CellViewLink, CellBasicNumber, CellBadge, getValueAsObject, CellViewLinkPresenter } from "@ui/Table/Cells";
import { WaIcon } from "@ui/WaIcon";
import { PlaylistSetPair, TrackPair, TrackSet } from "../../selectors";
import { messages } from "../../messages";
import { CellArtistsList } from "@app/modules/music/components/ArtistsList";

interface PlaylistTrendsTableOwnProps {
    data: PlaylistSetPair[];
    showTracks?: boolean;
}

type Props = PlaylistTrendsTableOwnProps;

// Type the table component
type TypedTable = typeof Table<PlaylistSetPair, TrackPair>;
const TypedTable = Table as TypedTable;

export const CellPlaylistName = (props: components.CellProps) => {
    const playlist = new Playlist(props.value.toJS());
    if (playlist.isTracked) {
        return <CellViewLink {...props} />;
    }
    return (
        <span>{playlist.name != null && playlist.name !== "" ? playlist.name : playlist.vendorId}</span>
    );
};

export const CellPlaylistNameLink = ({ value }: components.CellProps) => {
    const playlist = new Playlist(getValueAsObject(value));
    const playlistName = playlist.name != null && playlist.name !== "" ? playlist.name : playlist.vendorId;
    if (playlist.url != null) {
        return (
            <a href={playlist.url} target="_blank">
                {playlistName} <WaIcon name="external-link" size="sm" />
            </a>
        );
    }
    return (
        <span>{playlistName}</span>
    );
};

export const CellOwner = ({ value }: components.CellProps) => {
    if (value == null) {
        return <span></span>;
    }
    const owner = value.toJS();
    const name: string = owner.name ? owner.name : owner.id;
    if (owner.url) {
        return <a href={owner.url} target="_blank">{name}</a>;
    }
    return <span>{name}</span>;
};

export const CellPlaylistLink = ({ value }: { value: string }) => {
    return (
        <ButtonGroup>
            {value != null &&
                <Button tag="a" href={value} size="sm" outline target="_blank">
                    <FormattedMessage {...vMessages.goToPlaylist} />
                </Button>
            }
        </ButtonGroup>
    );
};

export const CellTracks = ({ value }: { value: Map<string, any> }) => {
    // Store whether it's expanded in the state
    const [expanded, setExpanded] = useState(false);

    const ts: TrackSet = value != null
        ? value.toJS() as TrackSet
        : {count: 0, items: []};
    const additionalTracks = ts.count - ts.items.length;
    const items = expanded ? ts.items : ts.items.slice(0, 1);
    const els: JSX.Element[] = items.map(({ entity, total, percentage }, key) => {
        entity = new Track(entity);
        const hasArtist = entity.artistsStr != null;
        const hasCount = percentage < 1;
        const text = (hasArtist ? entity.artistsStr + " - " : "") + entity.getTitle();
        const className = hasCount  ? "cell-tracks__item--has-count" : "";
        return (
            <li key={"track" + key} className={className}>
                <Link to={entity.getViewUrl()} className="cell-tracks__link" title={text}>
                    {hasArtist && <span className="cell-tracks__artist">{entity.artistsStr}</span>}
                    {hasArtist && <span>&nbsp;-&nbsp;</span>}
                    <span className="cell-tracks__title">{entity.getTitle()}</span>
                </Link>
                {hasCount &&
                    <span>&nbsp;(
                        <FormattedNumber value={total} />
                    )</span>
                }
            </li>
        );
    });
    if (expanded && additionalTracks > 0) {
        els.push(
            <li key="additional-tracks">
                <FormattedMessage {...messages.plusAdditionalTracks} values={{number: additionalTracks}} />
            </li>,
        );
    }
    if (ts.items.length > 1) {
        const linkMessage = expanded ? uiMessages.buttonShowFewer : uiMessages.buttonShowMore;
        els.push(
            <li key="expand-link">
                <Button
                    outline
                    size="sm"
                    tag="a"
                    href="#"
                    onClick={(e) => { e.preventDefault(); setExpanded(!expanded); }}
                >
                    <FormattedMessage {...linkMessage} />
                </Button>
            </li>,
        );
    }
    return <ol className="cell-tracks">{els}</ol>;
};

const PlaylistTrendsTable = ({ data, showTracks }: Props) => {
    const { formatMessage } = useIntl();
    const ExpandableCellComponent = useRef(makeExpandableCellComponent({
        mainMap: {
            rank: "rank",
            name: "entity",
            vendor: "entity.vendorName",
            total: "total",
            tracks: "trackSet.count",
        },
        components: {
            rank: null,
            name: CellPlaylistNameLink,
            vendor: CellBadge,
            total: CellBasicNumber,
            tracks: CellBasicNumber,
        },
        subHeadings: {
            rank: null,
            name: mMessages.trackEntitySingular,
            vendor: mMessages.artistEntityPlural,
            total: messages.salesStreams,
            tracks: null,
        },
        subMap: {
            rank: null,
            name: "entity.title",
            vendor: "entity.artists",
            total: "total",
            tracks: null,
        },
        subComponents: {
            rank: null,
            name: CellViewLinkPresenter,
            vendor: CellArtistsList,
            total: CellBasicNumber,
            tracks: null,
        },
    }));
    return (
        <TypedTable
            data={data}
            layout="basic"
            size="small"
            pageSize={data.length}
            getId={({ entity: { id } }) => String(id)}
            getSubRows={(entity) => entity.trackSet.items.map((tp) => ({
                entity,
                id: String(tp.entity.id),
                subEntity: tp,
            }))}
            getColspan={(row, columnId) => (
                row.isSubHeading || row.subEntity != null
                    ? {
                        rank: 1,
                        name: 1,
                        vendor: 2,
                        total: 1,
                        tracks: 1,
                    }[columnId]
                    : 1
            )}
            displaySubHeading
        >
            <RowDefinition>
                <ColumnDefinition
                    key="rank"
                    id="rank"
                    title="#"
                    customComponent={ExpandableCellComponent.current}
                />
                <ColumnDefinition
                    key="name"
                    id="name"
                    title={formatMessage(vMessages.playlistEntitySingular)}
                    customComponent={ExpandableCellComponent.current}
                    extraData={{entity: "track"}}
                />
                <ColumnDefinition
                    key="vendor"
                    id="vendor"
                    title={formatMessage(vMessages.trackColumnVendor)}
                    customComponent={ExpandableCellComponent.current}
                />
                <ColumnDefinition
                    key="total"
                    id="total"
                    title={formatMessage(messages.salesStreams)}
                    customComponent={ExpandableCellComponent.current}
                />
                {showTracks && <ColumnDefinition
                    key="tracks"
                    id="tracks"
                    title={formatMessage(messages.playlistTrendsTableTracks)}
                    customComponent={ExpandableCellComponent.current}
                />}
                {showTracks && <ColumnDefinition
                    key="controls"
                    id="controls"
                    title=" "
                    sortable={false}
                    customComponent={CellExpandButton}
                />}
            </RowDefinition>
        </TypedTable>
    );
};

export {
    PlaylistTrendsTable,
};
